import Vue from 'vue'
import Vuex from 'vuex'
import { recipesApi, ordersApi } from '@/utils/api'
import authModule from './modules/auth'
import messagesModule from './modules/messages'

Vue.use(Vuex)

export default new Vuex.Store({
  modules: {
    auth: authModule,
    messages: messagesModule
  },

  state: {
    // 菜品库
    recipes: [],

    // 菜篮子（当前选中，纯前端状态）
    basket: [],

    // 历史订单
    orderHistory: [],

    // 待处理订单（管理员视图）
    pendingOrders: [],

    // 当前激活分类
    activeCategory: 'all',

    // 搜索关键词
    searchKeyword: '',

    // 菜篮子弹窗显示
    showBasketModal: false,

    // 全局提示
    toast: null,

    // 加载状态
    loading: false
  },

  getters: {
    // 过滤后的菜品列表（本地过滤，避免每次搜索都请求接口）
    filteredRecipes(state) {
      let list = state.recipes
      if (state.activeCategory !== 'all') {
        list = list.filter(r => r.category === state.activeCategory)
      }
      if (state.searchKeyword.trim()) {
        const kw = state.searchKeyword.trim().toLowerCase()
        list = list.filter(r =>
          r.name.toLowerCase().includes(kw) ||
          r.ingredients.toLowerCase().includes(kw)
        )
      }
      return list
    },

    // 菜篮子总数
    basketTotal(state) {
      return state.basket.reduce((sum, item) => sum + item.count, 0)
    },

    // 历史订单（按时间倒序，后端已排序，此处保持一致）
    sortedOrderHistory(state) {
      return state.orderHistory
    },

    // 待处理订单（后端已排序）
    sortedPendingOrders(state) {
      return state.pendingOrders
    }
  },

  mutations: {
    SET_RECIPES(state, recipes) {
      state.recipes = recipes
    },

    SET_ORDER_HISTORY(state, history) {
      state.orderHistory = history
    },

    SET_PENDING_ORDERS(state, orders) {
      state.pendingOrders = orders
    },

    SET_LOADING(state, val) {
      state.loading = val
    },

    // ===== 菜篮子（纯前端，无需持久化）=====
    ADD_TO_BASKET(state, recipe) {
      const existing = state.basket.find(i => i.recipeId === recipe.id)
      if (existing) {
        existing.count++
      } else {
        state.basket.push({ recipeId: recipe.id, name: recipe.name, count: 1 })
      }
    },

    REMOVE_FROM_BASKET(state, recipeId) {
      const idx = state.basket.findIndex(i => i.recipeId === recipeId)
      if (idx !== -1) {
        if (state.basket[idx].count > 1) {
          state.basket[idx].count--
        } else {
          state.basket.splice(idx, 1)
        }
      }
    },

    CLEAR_BASKET(state) {
      state.basket = []
    },

    SET_BASKET_MODAL(state, val) {
      state.showBasketModal = val
    },

    // ===== 分类 & 搜索 =====
    SET_CATEGORY(state, cat) {
      state.activeCategory = cat
    },

    SET_SEARCH(state, kw) {
      state.searchKeyword = kw
    },

    // ===== Toast =====
    SET_TOAST(state, msg) {
      state.toast = msg
    }
  },

  actions: {
    // ===== 初始化：应用启动时拉取数据 =====
    async initApp({ dispatch, getters }) {
      // 如果有 token，验证并刷新用户信息
      if (getters['auth/isLoggedIn']) {
        await dispatch('auth/fetchMe')
        dispatch('messages/fetchUnreadCount')
      }
      await Promise.all([
        dispatch('fetchRecipes'),
        dispatch('fetchOrderHistory'),
        dispatch('fetchPendingOrders')
      ])
    },

    // ===== 菜品 =====
    async fetchRecipes({ commit }) {
      try {
        const recipes = await recipesApi.getAll()
        commit('SET_RECIPES', recipes)
      } catch (e) {
        console.error('获取菜品失败', e)
      }
    },

    async addRecipe({ commit, state, dispatch }, recipeData) {
      const recipe = await recipesApi.create(recipeData)
      commit('SET_RECIPES', [...state.recipes, recipe])
      dispatch('showToast', `已添加菜品：${recipe.name}`)
      return recipe
    },

    async updateRecipe({ commit, state, dispatch }, recipeData) {
      const updated = await recipesApi.update(recipeData.id, recipeData)
      const recipes = state.recipes.map(r => r.id === updated.id ? updated : r)
      commit('SET_RECIPES', recipes)
      dispatch('showToast', `已更新：${updated.name}`)
    },

    async deleteRecipe({ commit, state, dispatch }, recipe) {
      await recipesApi.remove(recipe.id)
      commit('SET_RECIPES', state.recipes.filter(r => r.id !== recipe.id))
      dispatch('showToast', `已删除：${recipe.name}`)
    },

    // ===== 菜篮子 =====
    addToBasket({ commit, dispatch }, recipe) {
      commit('ADD_TO_BASKET', recipe)
      dispatch('showToast', `已加入 ${recipe.name}`)
    },

    // ===== 订单 =====
    async fetchOrderHistory({ commit, getters }) {
      if (!getters['auth/isLoggedIn']) {
        commit('SET_ORDER_HISTORY', [])
        return
      }
      try {
        const history = await ordersApi.getHistory()
        commit('SET_ORDER_HISTORY', history)
      } catch (e) {
        console.error('获取历史订单失败', e)
      }
    },

    async fetchPendingOrders({ commit, getters }) {
      if (!getters['auth/isAdmin']) {
        commit('SET_PENDING_ORDERS', [])
        return
      }
      try {
        const pending = await ordersApi.getPending()
        commit('SET_PENDING_ORDERS', pending)
      } catch (e) {
        console.error('获取待处理订单失败', e)
      }
    },

    async submitOrder({ commit, state, dispatch }, { remark = '' } = {}) {
      if (!state.basket.length) return
      const order = await ordersApi.submit({
        items: state.basket,
        remark
      })
      commit('SET_ORDER_HISTORY', [order, ...state.orderHistory])
      commit('SET_PENDING_ORDERS', [order, ...state.pendingOrders])
      commit('CLEAR_BASKET')
      commit('SET_BASKET_MODAL', false)
      dispatch('showToast', '点菜成功！厨师正在准备中 🍳')
    },

    async completeOrder({ commit, state, dispatch }, orderId) {
      await ordersApi.complete(orderId)
      const pending = state.pendingOrders.filter(o => o.id !== orderId)
      const history = state.orderHistory.map(o =>
        o.id === orderId ? { ...o, isCompleted: true } : o
      )
      commit('SET_PENDING_ORDERS', pending)
      commit('SET_ORDER_HISTORY', history)
      dispatch('showToast', '订单已标记完成')
    },

    async deleteOrderHistory({ commit, state, dispatch }, orderId) {
      await ordersApi.remove(orderId)
      commit('SET_ORDER_HISTORY', state.orderHistory.filter(o => o.id !== orderId))
      dispatch('showToast', '已删除该记录')
    },

    // ===== Toast =====
    showToast({ commit }, msg) {
      commit('SET_TOAST', msg)
      setTimeout(() => commit('SET_TOAST', null), 2500)
    }
  }
})
